import { useState, useEffect } from 'react';
import { Button, Icon, Input } from '@/components/atoms';
import { useSalesList, useSaleDetail, useCreateReturnMutation } from '@/hooks/useErpApi';
import type {
  SaleResponse,
  CreateReturnRequest,
  CreateReturnItemRequest,
} from '@kisanlink/erp-service';
import { formatCurrency, formatDate } from '@/utils/formatters';

// Sale list response type (without items and breakdown)
type SaleListResponse = Omit<SaleResponse, 'items' | 'breakdown'>;

interface ReturnFormProps {
  saleId?: string;
  onSuccess: () => void;
  onCancel: () => void;
}

interface ReturnItemForm extends CreateReturnItemRequest {
  selected: boolean;
  maxQuantity: number;
  unitPrice: number;
}

export default function ReturnForm({ saleId, onSuccess, onCancel }: ReturnFormProps) {
  const [selectedSaleId, setSelectedSaleId] = useState<string>(saleId || '');
  const [selectedSale, setSelectedSale] = useState<SaleResponse | null>(null);
  const [returnItems, setReturnItems] = useState<ReturnItemForm[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitError, setSubmitError] = useState<string | null>(null);

  const { data: sales } = useSalesList({
    limit: 20,
  });

  // Fetch full sale details when a sale is selected (includes items and breakdown)
  const { data: saleDetailData } = useSaleDetail(selectedSaleId);

  const { mutateAsync: createReturn, isLoading: isSubmitting } = useCreateReturnMutation();

  // Load sale details when saleId is provided or selected
  useEffect(() => {
    if (selectedSaleId && saleDetailData) {
      const sale = saleDetailData;
      if (sale) {
        setSelectedSale(sale);

        // Initialize return items from sale items
        if (sale.items && sale.items.length > 0) {
          const items: ReturnItemForm[] = sale.items.map((item) => ({
            batch_id: item.batch_id,
            quantity: 0,
            refund_amount: 0,
            selected: false,
            maxQuantity: item.quantity,
            unitPrice: item.selling_price,
          }));
          setReturnItems(items);
        }
      }
    }
  }, [selectedSaleId, saleDetailData]);

  const handleSaleSelect = (sale: SaleListResponse) => {
    setSelectedSaleId(sale.id);
    setSearchQuery('');
    // Reset selected sale - will be populated when detail loads
    setSelectedSale(null);
    setReturnItems([]);
  };

  const handleItemToggle = (index: number) => {
    const newItems = [...returnItems];
    newItems[index].selected = !newItems[index].selected;
    if (newItems[index].selected && newItems[index].quantity === 0) {
      newItems[index].quantity = newItems[index].maxQuantity;
      newItems[index].refund_amount = newItems[index].maxQuantity * newItems[index].unitPrice;
    }
    setReturnItems(newItems);
  };

  const handleQuantityChange = (index: number, quantity: number) => {
    const newItems = [...returnItems];
    const maxQty = newItems[index].maxQuantity;

    if (quantity < 0) quantity = 0;
    if (quantity > maxQty) quantity = maxQty;

    newItems[index].quantity = quantity;
    newItems[index].refund_amount = quantity * newItems[index].unitPrice;
    setReturnItems(newItems);
  };

  const handleRefundAmountChange = (index: number, amount: number) => {
    const newItems = [...returnItems];
    if (amount < 0) amount = 0;
    newItems[index].refund_amount = amount;
    setReturnItems(newItems);
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!selectedSaleId) {
      newErrors.sale = 'Please select a sale';
    }

    const selectedItems = returnItems.filter((item) => item.selected);
    if (selectedItems.length === 0) {
      newErrors.items = 'Please select at least one item to return';
    }

    selectedItems.forEach((item, idx) => {
      if (item.quantity <= 0) {
        newErrors[`quantity_${idx}`] = 'Quantity must be greater than 0';
      }
      if (item.quantity > item.maxQuantity) {
        newErrors[`quantity_${idx}`] = `Quantity cannot exceed ${item.maxQuantity}`;
      }
      if (item.refund_amount < 0) {
        newErrors[`refund_${idx}`] = 'Refund amount cannot be negative';
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    const selectedItems = returnItems
      .filter((item) => item.selected)
      .map((item) => ({
        batch_id: item.batch_id,
        quantity: item.quantity,
        refund_amount: item.refund_amount,
      }));

    const returnRequest: CreateReturnRequest = {
      sale_id: selectedSaleId,
      items: selectedItems,
    };

    try {
      setSubmitError(null);
      await createReturn(returnRequest);
      onSuccess();
    } catch (error) {
      setSubmitError(
        error instanceof Error ? error.message : 'Failed to create return. Please try again.'
      );
    }
  };

  const totalRefund = returnItems
    .filter((item) => item.selected)
    .reduce((sum, item) => sum + item.refund_amount, 0);

  const filteredSales =
    sales?.filter((sale) => sale.id.toLowerCase().includes(searchQuery.toLowerCase())) || [];

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Error Display */}
      {submitError && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-start gap-2">
            <Icon name="CircleAlert" className="text-red-600 mt-0.5 h-5 w-5" />
            <p className="text-red-700">{submitError}</p>
          </div>
        </div>
      )}

      {/* Sale Selection */}
      {!saleId && (
        <div className="bg-white shadow rounded-lg p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Select Sale</h3>

          <Input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search Sale ID..."
            error={!!errors.sale}
            errorMessage={errors.sale}
          />

          {searchQuery && filteredSales.length > 0 && (
            <div className="mt-2 border rounded-md max-h-48 overflow-y-auto">
              {filteredSales.map((sale) => (
                <button
                  key={sale.id}
                  type="button"
                  onClick={() => handleSaleSelect(sale)}
                  className="w-full text-left px-4 py-2 hover:bg-gray-50 border-b last:border-b-0"
                >
                  <div className="flex justify-between">
                    <span className="font-medium">{sale.id.substring(0, 8)}</span>
                    <span className="text-gray-600">{formatDate(sale.sale_date)}</span>
                  </div>
                  <div className="text-sm text-gray-500">
                    {formatCurrency(sale.total_amount)}
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Sale Details */}
      {selectedSale && (
        <div className="bg-white shadow rounded-lg p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Sale Details</h3>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-sm font-medium text-gray-500">Sale ID</p>
              <p className="text-base text-gray-900">{selectedSale.id.substring(0, 8)}</p>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-500">Sale Date</p>
              <p className="text-base text-gray-900">{formatDate(selectedSale.sale_date)}</p>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-500">Payment Mode</p>
              <p className="text-base text-gray-900 capitalize">{selectedSale.payment_mode}</p>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-500">Total Amount</p>
              <p className="text-base font-bold text-gray-900">
                {formatCurrency(selectedSale.total_amount)}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Return Items */}
      {returnItems.length > 0 && (
        <div className="bg-white shadow rounded-lg p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Select Items to Return</h3>
          {errors.items && <p className="text-sm text-red-600 mb-4">{errors.items}</p>}

          <div className="space-y-4">
            {returnItems.map((item, index) => (
              <div
                key={index}
                className={`border rounded-lg p-4 ${
                  item.selected ? 'border-blue-500 bg-blue-50' : 'border-gray-200'
                }`}
              >
                <div className="flex items-start space-x-4">
                  <input
                    type="checkbox"
                    checked={item.selected}
                    onChange={() => handleItemToggle(index)}
                    className="mt-1 h-4 w-4 text-blue-600 rounded"
                  />

                  <div className="flex-1 space-y-3">
                    <div className="grid grid-cols-3 gap-4">
                      <div>
                        <p className="text-sm font-medium text-gray-700">Batch ID</p>
                        <p className="text-sm text-gray-900">{item.batch_id.substring(0, 8)}</p>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-700">Max Quantity</p>
                        <p className="text-sm text-gray-900">{item.maxQuantity}</p>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-700">Unit Price</p>
                        <p className="text-sm text-gray-900">{formatCurrency(item.unitPrice)}</p>
                      </div>
                    </div>

                    {item.selected && (
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Return Quantity
                          </label>
                          <input
                            type="number"
                            min="0"
                            max={item.maxQuantity}
                            value={item.quantity}
                            onChange={(e) =>
                              handleQuantityChange(index, parseInt(e.target.value) || 0)
                            }
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          />
                          {errors[`quantity_${index}`] && (
                            <p className="text-sm text-red-600 mt-1">
                              {errors[`quantity_${index}`]}
                            </p>
                          )}
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Refund Amount
                          </label>
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            value={item.refund_amount}
                            onChange={(e) =>
                              handleRefundAmountChange(index, parseFloat(e.target.value) || 0)
                            }
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          />
                          {errors[`refund_${index}`] && (
                            <p className="text-sm text-red-600 mt-1">{errors[`refund_${index}`]}</p>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Summary */}
      {returnItems.some((item) => item.selected) && (
        <div className="bg-white shadow rounded-lg p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Return Summary</h3>
          <div className="space-y-2">
            <div className="flex justify-between">
              <span className="text-gray-600">Items to Return</span>
              <span className="font-medium">
                {returnItems.filter((item) => item.selected).length}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Total Quantity</span>
              <span className="font-medium">
                {returnItems
                  .filter((item) => item.selected)
                  .reduce((sum, item) => sum + item.quantity, 0)}
              </span>
            </div>
            <div className="flex justify-between pt-2 border-t">
              <span className="text-lg font-semibold">Total Refund</span>
              <span className="text-lg font-bold text-blue-600">{formatCurrency(totalRefund)}</span>
            </div>
          </div>
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex justify-end space-x-4">
        <Button type="button" variant="ghost" onClick={onCancel} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button
          type="submit"
          variant="primary"
          disabled={
            !selectedSale || returnItems.filter((i) => i.selected).length === 0 || isSubmitting
          }
          loading={isSubmitting}
          loadingText="Creating Return..."
        >
          Submit Return
        </Button>
      </div>
    </form>
  );
}
